import React from "react";
import { Text, View } from "native-base";
import { CheckCircle2, History } from "lucide-react-native";
import { webFont } from "../../../Universal/API/API";
import PressableWrapp from "../../../Universal/Buttons/PressableReact/PressableWrapp";
import type { OrderHistorySummary } from "../../cartTypes";
import { formatCurrency, formatOrderHistoryDate } from "../../helpers";
import { getOrderCardTotal, webCursorPointer } from "./helpers";

type OrderCardProps = {
  scaleAll: number;
  order: OrderHistorySummary;
  showDiscountPrices: boolean;
  isSelected: boolean;
  onPress: () => void;
};

const OrderCard: React.FC<OrderCardProps> = ({
  scaleAll,
  order,
  showDiscountPrices,
  isSelected,
  onPress,
}) => {
  const total = getOrderCardTotal({ order, showDiscountPrices });
  const dateText = formatOrderHistoryDate(order.updated_at || order.created_at);
  const statusText = order.status === "submitted" ? "оформлен" : "сохранён";

  return (
    <PressableWrapp
      onPress={onPress}
      style={[
        {
          borderRadius: 18 * scaleAll,
          backgroundColor: isSelected ? "#111111" : "#FFFFFF",
          borderWidth: 1,
          borderColor: isSelected ? "#111111" : "#ECE8E1",
          paddingHorizontal: 14 * scaleAll,
          paddingVertical: 12 * scaleAll,
          marginBottom: 10 * scaleAll,
        },
        webCursorPointer,
      ]}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12 * scaleAll,
        }}
      >
        <View style={{ flexDirection: "row", alignItems: "center", flex: 1, minWidth: 0 }}>
          <View
            style={{
              width: 34 * scaleAll,
              height: 34 * scaleAll,
              borderRadius: 12 * scaleAll,
              backgroundColor: isSelected ? "rgba(255,255,255,0.14)" : "#F7F5F0",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 10 * scaleAll,
            }}
          >
            <History size={18 * scaleAll} color={isSelected ? "#FFFFFF" : "#111111"} />
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: "row", alignItems: "center", marginBottom: 4 * scaleAll }}>
              <View
                style={{
                  borderRadius: 999,
                  backgroundColor: isSelected
                    ? "rgba(255,255,255,0.14)"
                    : order.status === "submitted"
                      ? "#EEF8F1"
                      : "#F7F5F0",
                  paddingHorizontal: 8 * scaleAll,
                  paddingVertical: 4 * scaleAll,
                  marginRight: 8 * scaleAll,
                }}
              >
                <Text
                  selectable={false}
                  style={{
                    fontFamily: webFont("Inter"),
                    fontSize: 10 * scaleAll,
                    color: isSelected
                      ? "#FFFFFF"
                      : order.status === "submitted"
                        ? "#1D7A3F"
                        : "#6E665D",
                    fontWeight: "700",
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                  }}
                >
                  {statusText}
                </Text>
              </View>
              {order.status === "submitted" ? (
                <CheckCircle2 size={13 * scaleAll} color={isSelected ? "#FFFFFF" : "#1D7A3F"} />
              ) : null}
            </View>
            <Text
              selectable={false}
              numberOfLines={1}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 14 * scaleAll,
                color: isSelected ? "#FFFFFF" : "#111111",
                fontWeight: "700",
              }}
            >
              {order.order_id}
            </Text>
            <Text
              selectable={false}
              style={{
                fontFamily: webFont("Inter"),
                fontSize: 11 * scaleAll,
                color: isSelected ? "rgba(255,255,255,0.72)" : "#7A746C",
                fontWeight: "600",
                marginTop: 2 * scaleAll,
              }}
            >
              {dateText || "Без даты"} • {order.total_quantity} шт. • {order.items_count} поз.
            </Text>
          </View>
        </View>

        <View
          style={{
            alignItems: "flex-end",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Text
            selectable={false}
            style={{
              fontFamily: webFont("Inter"),
              fontSize: 18 * scaleAll,
              color: isSelected ? "#FFFFFF" : "#111111",
              fontWeight: "700",
            }}
          >
            {formatCurrency(total)} ₽
          </Text>
          <Text
            selectable={false}
            style={{
              fontFamily: webFont("Inter"),
              fontSize: 11 * scaleAll,
              color: isSelected ? "rgba(255,255,255,0.72)" : "#7A746C",
              fontWeight: "600",
              marginTop: 2 * scaleAll,
            }}
          >
            {order.order_name || "Без названия"}
          </Text>
        </View>
      </View>
    </PressableWrapp>
  );
};

export default React.memo(OrderCard);
